// GwbkAutogenJobView — the read-only detail body for a single autogen job:
// progress bar, stage status, the planned-dashboard cards (with per-dashboard
// build status: built / failed / building / queued), a duplicates roll-up, all
// captured screenshots, and the log. It's purely presentational — the host
// (the run dialog or the dedicated autogen page) owns fetching/polling the job
// and rendering action buttons (Stop / Download / Delete).

import { Loader2 } from 'lucide-react'
import type { GwbkAutogenJob } from '@axilon-apps/domains-ontology-ui-state'

import { Progress } from '../../components/ui/progress'
import GwbkAutogenPlanCard from './GwbkAutogenPlanCard'
import GwbkAutogenShotThumb from './GwbkAutogenShotThumb'
import { isActiveState } from './gwbk-autogen-status'

export interface GwbkAutogenJobViewProps {
  job: GwbkAutogenJob
}

const GwbkAutogenJobView = ({ job }: GwbkAutogenJobViewProps) => {
  const isBuilding = job.state === 'building'
  const isActive = isActiveState(job.state)

  const progressPct =
    job.total > 0 ? Math.round((job.completed / job.total) * 100) : isActive ? 5 : 0

  const shotsByName = new Map(job.shots.map((shot) => [shot.name, shot]))

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 py-1">
      <div className="flex items-center gap-3">
        <Progress value={progressPct} className="flex-1" />
        <span className="font-mono text-xs text-ink-3">{`${job.completed}/${job.total}`}</span>
      </div>

      <div className="flex items-center gap-2 text-sm">
        {job.state === 'capturing' ? (
          <>
            <Loader2 className="size-4 animate-spin text-brand" aria-hidden />
            <span className="text-ink-2">Capturing screenshots…</span>
          </>
        ) : job.state === 'planning' ? (
          <>
            <Loader2 className="size-4 animate-spin text-brand" aria-hidden />
            <span className="text-ink-2">Planning dashboards from the screenshots…</span>
          </>
        ) : isBuilding ? (
          <>
            <Loader2 className="size-4 animate-spin text-brand" aria-hidden />
            <span className="text-ink-2">
              Building dashboards with live data
              {job.plan
                ? ` (${(job.builds ?? []).filter(Boolean).length}/${job.plan.dashboards.length})`
                : ''}
              …
            </span>
          </>
        ) : job.state === 'done' ? (
          <span className="text-ink-2">
            Done — {job.shots.length} screenshot{job.shots.length === 1 ? '' : 's'} captured
            {job.plan ? `, ${job.plan.dashboards.length} planned` : ''}
            {job.plan && job.builds && job.builds.length > 0
              ? `, ${job.builds.filter((b) => b?.completed).length}/${job.plan.dashboards.length} built`
              : ''}
            .
          </span>
        ) : job.state === 'cancelled' ? (
          <span className="text-ink-3">Cancelled.</span>
        ) : job.state === 'error' ? (
          <span className="text-destructive">{job.error ?? 'Capture failed.'}</span>
        ) : null}
      </div>

      {job.plan && job.plan.dashboards.length > 0 ? (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pr-1">
          {job.plan.dashboards.map((dash, di) => {
            // Builds are positionally aligned with the plan, so `builds[di]` is
            // this dashboard's outcome. While building, slots split three ways:
            // done (`build` set), actively building (index in `buildingIndices`),
            // or queued (waiting for a free slot).
            const build = job.builds?.[di]
            const buildActive = isBuilding && !build && (job.buildingIndices?.includes(di) ?? false)
            const buildQueued = isBuilding && !build && !buildActive
            return (
              <GwbkAutogenPlanCard
                key={`${dash.title}-${di}`}
                jobId={job.id}
                dash={dash}
                build={build}
                buildActive={buildActive}
                buildQueued={buildQueued}
                shotsByName={shotsByName}
              />
            )
          })}

          {job.plan.duplicates.length > 0 ? (
            <details className="rounded border border-line bg-bg-sunk/30">
              <summary className="cursor-pointer px-2.5 py-1.5 text-xs text-ink-3">
                Duplicates skipped ({job.plan.duplicates.length})
              </summary>
              <div className="flex flex-col gap-1 px-2.5 pb-2 font-mono text-11 text-ink-3">
                {job.plan.duplicates.map((dup) => (
                  <div key={dup.name} className="truncate" title={dup.reason}>
                    {shotsByName.get(dup.name)?.routeKey ?? dup.name} → keep{' '}
                    {shotsByName.get(dup.duplicateOf)?.routeKey ?? dup.duplicateOf}
                  </div>
                ))}
              </div>
            </details>
          ) : null}

          {job.plan.discarded && job.plan.discarded.length > 0 ? (
            <details className="rounded border border-line bg-bg-sunk/30">
              <summary className="cursor-pointer px-2.5 py-1.5 text-xs text-ink-3">
                Discarded — still loading / blank ({job.plan.discarded.length})
              </summary>
              <div className="flex flex-col gap-1 px-2.5 pb-2 font-mono text-11 text-ink-3">
                {job.plan.discarded.map((junk) => (
                  <div key={junk.name} className="truncate" title={junk.reason}>
                    {shotsByName.get(junk.name)?.routeKey ?? junk.name}
                    {junk.reason ? ` — ${junk.reason}` : ''}
                  </div>
                ))}
              </div>
            </details>
          ) : null}

          <details className="rounded border border-line bg-bg-sunk/30">
            <summary className="cursor-pointer px-2.5 py-1.5 text-xs text-ink-3">
              All screenshots ({job.shots.length})
            </summary>
            <div className="grid grid-cols-2 gap-2 px-2.5 pb-2 sm:grid-cols-4">
              {job.shots.map((shot) => (
                <GwbkAutogenShotThumb
                  key={shot.name}
                  jobId={job.id}
                  shot={shot}
                  caption={`${shot.routeKey}${shot.dynamic ? ' (sampled)' : ''}`}
                />
              ))}
            </div>
          </details>
        </div>
      ) : job.shots.length > 0 ? (
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-3 overflow-auto pr-1 sm:grid-cols-3">
          {job.shots.map((shot) => (
            <GwbkAutogenShotThumb
              key={shot.name}
              jobId={job.id}
              shot={shot}
              caption={`${shot.routeKey}${shot.dynamic ? ' (sampled)' : ''}`}
              imgHeightClass="h-24"
              className="group p-1.5"
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center text-sm text-ink-3">
          {isActive ? 'Waiting for the first screenshot…' : 'No screenshots captured.'}
        </div>
      )}

      {job.logs.length > 0 ? (
        <details className="rounded border border-line bg-bg-sunk/30">
          <summary className="cursor-pointer px-2.5 py-1.5 text-xs text-ink-3">
            Log ({job.logs.length})
          </summary>
          <div className="max-h-32 overflow-auto px-2.5 pb-2 font-mono text-11 text-ink-3">
            {job.logs.map((entry, i) => (
              <div key={i} className={entry.level === 'error' ? 'text-destructive' : undefined}>
                {entry.message}
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  )
}

export default GwbkAutogenJobView
